'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { LoadingIcon } from '@insta-club/components/icons';
import { PaymentResponse } from '@insta-club/models';
import { t } from '@insta-club/translations';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';

interface PayDialogProps {
    open: boolean;
    gateway: string;
    onClose: () => void;
    amount?: number | null;
    payWithWallet: boolean;
    invoiceId?: number | null;
}

const PayDialog: React.FC<PayDialogProps> = ({ open, onClose, amount, gateway, invoiceId, payWithWallet }) => {
    const router = useRouter();

    const createPay = useCallback(async () => {
        try {
            const res: PaymentResponse = await fetcher(ApiKeys.POST_PAYMENT, 'dashboard', 'POST', {
                body: {
                    amount,
                    invoice_id: invoiceId,
                    online_pay: !payWithWallet,
                    gateway: gateway === 'wallet' ? null : gateway
                }
            });
            if (res?.action) {
                window.location.href = res.action;
            } else {
                router.replace(`/payment/callback/wallet?refid=${res}`);
            }
        } catch {
            toast.error(t('please_try_again'));
            onClose();
        }
    }, [amount, gateway, invoiceId, payWithWallet, onClose, router]);

    useEffect(() => {
        if (open) {
            createPay();
        }
    }, [open, createPay]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key='pay-dialog'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    className='fixed inset-0 z-50 container flex flex-col items-center justify-center bg-white'
                >
                    <LoadingIcon className='h-10 w-10 animate-spin text-gray-500' />
                    <span className='mt-6 font-semibold text-gray-800'>
                        {payWithWallet ? t('withdrawing_from_wallet') : t('transfer_to_bank_portal')}
                    </span>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PayDialog;

